import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  HStack,
  VStack,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Icon,
  Input,
  Select,
  FormControl,
  FormLabel,
  useColorModeValue
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import InvoiceList from '../components/InvoiceList';
import InvoiceModal from '../components/InvoiceModal';
import ProductDetailModal from '../components/ProductDetailModal';
import usePagination from '../utils/usePagination';
import { getVisiblePageNumbers } from '../utils/paginationHelpers';
import api from '../api';

const ReturnInvoicesPage = () => {
  // Color mode values for consistent theming
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const productHoverBg = useColorModeValue('blue.50', 'blue.900');

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null); // For displaying invoice details
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [invoiceCache, setInvoiceCache] = useState(new Map()); // Cache for detailed invoice data
  const [modalKey, setModalKey] = useState(Date.now()); // Force modal remount
  
  // Product detail modal states
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProductDetailModalOpen, setIsProductDetailModalOpen] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    start_date: '',
    end_date: '',
    total_min: '',
    total_max: '',
    status: ''
  });

  // Status translation mappings
  const statusOptions = [
    { value: '', label: 'Всі статуси' },
    { value: 'new', label: 'новий' },
    { value: 'pending', label: 'в очікуванні' },
    { value: 'completed', label: 'проведено' }
  ];

  const parseTotal = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const totalWithoutVat = parseTotal(selectedInvoice?.total_without_vat || 0);
  const vat = parseTotal(selectedInvoice?.vat || 0);
  const totalWithVat = parseTotal(selectedInvoice?.total_with_vat || 0);

  const baseUrl = '/return-invoices/';

  const fetchInvoices = async (url = `${baseUrl}?page=1`) => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add page parameter if it exists in the URL
      const urlObj = new URL(url, window.location.origin);
      const pageParam = urlObj.searchParams.get('page');
      if (pageParam) {
        params.append('page', pageParam);
      }
      
      // Add filter parameters
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }
      if (filters.total_min) {
        params.append('total_min', filters.total_min);
      }
      if (filters.total_max) {
        params.append('total_max', filters.total_max);
      }
      if (filters.status) {
        params.append('status', filters.status);
      }
      
      const response = await api.get(`${baseUrl}?${params.toString()}`);
      setInvoices(response.data.results);
      updatePagination(response.data);
    } catch (error) {
      console.error('Failed to fetch return invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const {
    pagination,
    updatePagination,
    fetchNext: originalFetchNext,
    fetchPrevious: originalFetchPrevious,
    goToPage: originalGoToPage,
  } = usePagination(fetchInvoices, baseUrl);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(pagination.count / (pagination.pageSize || 1))),
    [pagination.count, pagination.pageSize],
  );

  const visiblePages = useMemo(
    () => getVisiblePageNumbers(pagination.currentPage, totalPages),
    [pagination.currentPage, totalPages],
  );

  // Custom pagination functions that preserve filters
  const buildPaginationUrl = (page) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    
    // Add filter parameters
    if (filters.search) params.append('search', filters.search);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.total_min) params.append('total_min', filters.total_min);
    if (filters.total_max) params.append('total_max', filters.total_max);
    if (filters.status) params.append('status', filters.status);
    
    return `${baseUrl}?${params.toString()}`;
  };

  const fetchNext = () => {
    if (pagination.next) {
      const nextPage = pagination.currentPage + 1;
      fetchInvoices(buildPaginationUrl(nextPage));
    }
  };

  const fetchPrevious = () => {
    if (pagination.previous && pagination.currentPage > 1) {
      const prevPage = pagination.currentPage - 1;
      fetchInvoices(buildPaginationUrl(prevPage));
    }
  };

  const goToPage = (page) => {
    if (page >= 1) {
      fetchInvoices(buildPaginationUrl(page));
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Trigger search when filters change (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Always start from page 1 when filters change
      const params = new URLSearchParams();
      params.append('page', '1');
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.total_min) params.append('total_min', filters.total_min);
      if (filters.total_max) params.append('total_max', filters.total_max);
      if (filters.status) params.append('status', filters.status);
      
      const url = `${baseUrl}?${params.toString()}`;
      fetchInvoices(url);
    }, filters.search ? 300 : 0); // 300ms debounce for search, immediate for other filters

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      start_date: '',
      end_date: '',
      total_min: '',
      total_max: '',
      status: ''
    });
  };

  const handleCreateClick = () => {
    setEditingInvoice(null);
    setModalKey(Date.now()); // Force modal remount
    setIsCreateModalOpen(true);
  };

  const handleEditInvoice = async (invoiceId) => {
    try {
      // Check cache first
      if (invoiceCache.has(invoiceId)) {
        setEditingInvoice(invoiceCache.get(invoiceId));
        setModalKey(Date.now()); // Force modal remount
        setIsCreateModalOpen(true);
        return;
      }

      const response = await api.get(`${baseUrl}${invoiceId}/`);
      const invoiceData = response.data;
      
      // Cache the detailed data
      setInvoiceCache(prev => new Map(prev).set(invoiceId, invoiceData));
      
      setEditingInvoice(invoiceData);
      setModalKey(Date.now()); // Force modal remount
      setIsCreateModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleInvoiceCreated = async (invoiceData, isUpdate = false) => {
    if (isUpdate) {
      // Update existing invoice in list
      setInvoices((prev) =>
        prev.map((inv) => (inv.id === invoiceData.id ? invoiceData : inv))
      );
      
      // Update cache
      setInvoiceCache(prev => new Map(prev).set(invoiceData.id, invoiceData));
    } else {
      // Add new invoice to list
      setInvoices((prev) => [invoiceData, ...prev]);
      
      // Fetch detailed data and cache it
      try {
        const response = await api.get(`${baseUrl}${invoiceData.id}/`);
        const detailedData = response.data;
        setInvoiceCache(prev => new Map(prev).set(invoiceData.id, detailedData));
        
        // Update the invoice in the list with detailed data
        setInvoices((prev) =>
          prev.map((inv) => (inv.id === invoiceData.id ? detailedData : inv))
        );
      } catch (error) {
        console.error('Failed to sync invoice:', error);
      }
    }
  };

  const handleDeleteInvoice = async (id) => {
    try {
      await api.delete(`${baseUrl}${id}/`);
      fetchInvoices();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
    }
  };

  const handleViewDetails = async (invoiceId) => {
    try {
      // Check cache first
      if (invoiceCache.has(invoiceId)) {
        setSelectedInvoice(invoiceCache.get(invoiceId));
        setIsDetailsModalOpen(true);
        return;
      }

      const response = await api.get(`${baseUrl}${invoiceId}/`);
      const invoiceData = response.data;
      
      // Cache the detailed data
      setInvoiceCache(prev => new Map(prev).set(invoiceId, invoiceData));
      
      setSelectedInvoice(invoiceData);
      setIsDetailsModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch details:', error);
    }
  };
  

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedInvoice(null);
  };

  const handleOpenProductDetail = (product) => {
    setSelectedProduct(product);
    setIsProductDetailModalOpen(true);
  };

  const handleCloseProductDetail = () => {
    setIsProductDetailModalOpen(false);
    setSelectedProduct(null);
  };

  const renderInvoiceForPrint = (invoiceData) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write('<html><head><title>Накладна</title></head><body style="font-family: Arial, sans-serif; font-size: 12px;">');
    printWindow.document.write('<h1 style="text-align: center; margin-bottom: 20px; font-size: 18px;">Накладна #' + invoiceData.id + '</h1>');
    printWindow.document.write('<table border="1" cellpadding="5" cellspacing="0" style="width: 100%; border-collapse: collapse; border-color: #ddd;">');
    printWindow.document.write('<tr><td colspan="5" style="background-color: #f0f0f0; border-bottom: 1px solid #ddd; font-size: 14px;">');
    printWindow.document.write('<p style="margin-bottom: 10px;">Дата створення: ' + new Date(invoiceData.created_at).toLocaleString() + '</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">Примітка: ' + (invoiceData.note || 'Без приміток') + '</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">Клієнт: ' + (invoiceData.customer_name || 'Без клієнта') + '</p>');
    printWindow.document.write('</td></tr>');
    printWindow.document.write('<tr style="background-color: #f0f0f0; border-bottom: 1px solid #ddd;"><th style="width: 10%;">№</th><th style="width: 40%;">Назва товару</th><th style="width: 15%;">Ціна</th><th style="width: 15%;">Кількість</th><th style="width: 20%;">Сума</th></tr>');
    
    invoiceData.entries.forEach((entry, index) => {
      const price = parseFloat(entry.price);
      printWindow.document.write(
        `<tr style="border-bottom: 1px solid #ddd;"><td style="text-align: center;">${index + 1}</td><td>${entry.product.name}</td><td style="text-align: right;">${price.toFixed(2)} грн.</td><td style="text-align: center;">${entry.quantity}</td><td style="text-align: right;">${(price * entry.quantity).toFixed(2)} грн.</td></tr>`
      );
    });
    
    printWindow.document.write('</table>');
    printWindow.document.write('<div style="float: right; margin-top: 20px;">');
    printWindow.document.write('<p style="margin-bottom: 10px;">Разом без ПДВ: ' + invoiceData.total_without_vat + ' грн.</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">ПДВ: ' + invoiceData.vat + ' грн.</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">Всього з ПДВ: ' + invoiceData.total_with_vat + ' грн.</p>');
    printWindow.document.write('</div>');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };
  
  const handlePrintInvoice = async (invoiceId) => {
    try {
      // Check cache first
      let invoiceData;
      if (invoiceCache.has(invoiceId)) {
        invoiceData = invoiceCache.get(invoiceId);
      } else {
        const response = await api.get(`${baseUrl}${invoiceId}/`);
        invoiceData = response.data;
        setInvoiceCache(prev => new Map(prev).set(invoiceId, invoiceData));
      }
      
      renderInvoiceForPrint(invoiceData);
    } catch (error) {
      console.error('Failed to print invoice:', error);
    }
  };

  const handleChangeStatus = async (id, newStatus) => {
    try {
      await api.patch(`${baseUrl}${id}/`, { status: newStatus });
      
      // Update invoice in cache if it exists
      if (invoiceCache.has(id)) {
        const cachedInvoice = invoiceCache.get(id);
        const updatedInvoice = { ...cachedInvoice, status: newStatus };
        setInvoiceCache(prev => new Map(prev).set(id, updatedInvoice));
      }
      
      fetchInvoices();
    } catch (error) {
      console.error('Failed to change status:', error);
    }
  };

  return (
    <Box position="relative" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')} overflowX="hidden">
      <Box position="relative" zIndex={1} p={{ base: 2, md: 8 }}>
        {/* Modern Header Card */}
        <Box
          bg={bgColor}
          borderRadius={{ base: 'lg', md: '2xl' }}
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 8 }}
          mb={{ base: 4, md: 8 }}
          boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          position="relative"
          overflow="visible"
        >
          {/* Decorative gradient overlay - Red theme for returns */}
          <Box position="absolute" top={0} left={0} right={0} height="6px" bg="linear-gradient(90deg, #EF4444, #DC2626, #B91C1C)" />
          {/* Header section with icon and title */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={4} direction={{ base: 'column', md: 'row' }}>
            <Flex align="center">
              <Box p={3} bg="linear-gradient(135deg, #EF4444, #DC2626)" borderRadius="lg" mr={4} boxShadow="0 4px 6px -1px rgba(239, 68, 68, 0.3)">
                <Icon viewBox="0 0 24 24" boxSize={6} color="white">
                  <path fill="currentColor" d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/>
                </Icon>
              </Box>
              <Box>
                <Heading as="h1" size={{ base: 'lg', md: '2xl' }} bgGradient="linear(to-r, #EF4444, #DC2626)" bgClip="text" mb={2} fontWeight="bold">
                  Повернення
                </Heading>
                <Text color={textColor} fontSize={{ base: 'sm', md: 'lg' }}>
                  Управління поверненнями товарів
                </Text>
              </Box>
            </Flex>
            <Button
              size={{ base: 'md', md: 'lg' }}
              bg="linear-gradient(135deg, #10B981, #059669)"
              color="white"
              leftIcon={<AddIcon />}
              onClick={handleCreateClick}
              borderRadius="xl"
              px={{ base: 6, md: 8 }}
              boxShadow="0 4px 15px rgba(16, 185, 129, 0.4)"
              _hover={{ bg: 'linear-gradient(135deg, #059669, #047857)', transform: { base: 'none', md: 'translateY(-2px)' }, boxShadow: '0 8px 25px rgba(16, 185, 129, 0.6)', transition: 'all 0.2s ease' }}
              _active={{ transform: 'translateY(0)' }}
              fontWeight="600"
            >
              Створити повернення
            </Button>
          </Flex>
        </Box>

        {/* Modern Filters Card */}
        <Box
          bg={bgColor}
          borderRadius={{ base: 'lg', md: '2xl' }}
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 6 }}
          mb={{ base: 4, md: 8 }}
          boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
          position="relative"
          overflow="hidden"
        >
          {/* Decorative gradient overlay - Purple theme for filters */}
          <Box position="absolute" top={0} left={0} right={0} height="4px" bg="linear-gradient(90deg, #8B5CF6, #7C3AED, #6D28D9)" opacity={0.8} />
          
          <VStack spacing={4} align="stretch">
            <Text 
              fontSize="lg" 
              fontWeight="bold"
              bgGradient="linear(to-r, #8B5CF6, #7C3AED)"
              bgClip="text"
            >
              🔍 Фільтри та пошук
              {(filters.search || filters.start_date || filters.end_date || filters.total_min || filters.total_max || filters.status) && (
                <Text as="span" fontSize="sm" color="green.500" ml={2}>
                  (активні)
                </Text>
              )}
            </Text>
            
            {/* First row - Search */}
            <Box>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  Пошук
                </FormLabel>
                <Input
                  placeholder="🔍 Пошук за ID накладної, назвою товару, каталожним номером, клієнтом, статусом..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                  _placeholder={{
                    color: textColor,
                  }}
                />
              </FormControl>
            </Box>
            
            {/* Second row - Date, Amount and Status filters */}
            <HStack spacing={4} align="end">
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  📅 Дата від
                </FormLabel>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                />
              </FormControl>
              
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  📅 Дата до
                </FormLabel>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                />
              </FormControl>
              
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  💰 Сума від (грн)
                </FormLabel>
                <Input
                  type="number"
                  placeholder="Мін. сума"
                  value={filters.total_min}
                  onChange={(e) => handleFilterChange('total_min', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                />
              </FormControl>
              
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  💰 Сума до (грн)
                </FormLabel>
                <Input
                  type="number"
                  placeholder="Макс. сума"
                  value={filters.total_max}
                  onChange={(e) => handleFilterChange('total_max', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                />
              </FormControl>
              
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  📋 Статус
                </FormLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              
              <Button 
                onClick={clearFilters}
                bg="linear-gradient(135deg, #EF4444, #DC2626)"
                color="white"
                borderRadius="lg"
                px={6}
                boxShadow="0 4px 15px rgba(239, 68, 68, 0.4)"
                _hover={{
                  bg: "linear-gradient(135deg, #DC2626, #B91C1C)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(239, 68, 68, 0.6)",
                  transition: "all 0.2s ease"
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                fontWeight="600"
              >
                🗑️ Очистити
              </Button>
            </HStack>
          </VStack>
        </Box>

        {/* Content Area */}
          {loading ? (
            <Flex justify="center" align="center" minH="400px">
              <VStack spacing={4}>
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text color={textColor} fontSize="lg">
                  Завантаження накладних...
                </Text>
              </VStack>
            </Flex>
          ) : invoices.length ? (
            <InvoiceList
              invoices={invoices}
              onEdit={handleEditInvoice}
              onDelete={handleDeleteInvoice}
              onViewDetails={handleViewDetails}
              onPrintInvoice={handlePrintInvoice}
              onChangeStatus={handleChangeStatus}
            />
          ) : (
            <Flex justify="center" align="center" minH="400px">
              <VStack spacing={6}>
                <Box
                  p={6}
                  bg="linear-gradient(135deg, #EF4444, #DC2626)"
                  borderRadius="full"
                  boxShadow="0 4px 6px -1px rgba(239, 68, 68, 0.3)"
                >
                  <Icon viewBox="0 0 24 24" boxSize={12} color="white">
                    <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                  </Icon>
                </Box>
                <VStack spacing={2}>
                  <Text fontSize="xl" fontWeight="bold" color={textColor}>
                    Повернень не знайдено
                  </Text>
                  <Text color={textColor} textAlign="center" maxW="md">
                    {(filters.search || filters.start_date || filters.end_date || filters.total_min || filters.total_max || filters.status) 
                      ? "За вашими критеріями пошуку накладних не знайдено. Спробуйте змінити фільтри."
                      : "У системі ще немає жодної накладної повернення. Створіть першу накладну!"
                    }
                  </Text>
                  <Button
                    mt={4}
                    bg="linear-gradient(135deg, #10B981, #059669)"
                    color="white"
                    leftIcon={<AddIcon />}
                    onClick={handleCreateClick}
                    borderRadius="lg"
                    px={6}
                    boxShadow="0 4px 15px rgba(16, 185, 129, 0.4)"
                    _hover={{
                      bg: "linear-gradient(135deg, #059669, #047857)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 25px rgba(16, 185, 129, 0.6)",
                    }}
                  >
                    Створити накладну
                  </Button>
                </VStack>
              </VStack>
            </Flex>
          )}

        {/* Modern Pagination - Make responsive */}
        {pagination.count > 0 && totalPages > 1 && (
          <Box bg={bgColor} borderRadius="2xl" border="1px solid" borderColor={borderColor} p={{ base: 4, md: 6 }} mt={{ base: 6, md: 8 }} boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.1)">
            <Flex justify="center" align="center">
              <HStack spacing={{ base: 2, md: 3 }} flexWrap="wrap" justify="center">
                <Button onClick={() => fetchPrevious()} isDisabled={!pagination.previous || pagination.currentPage <= 1} bg="linear-gradient(135deg, #3B82F6, #2563EB)" color="white" borderRadius="xl" size={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 6 }} _hover={{ bg: 'linear-gradient(135deg, #2563EB, #1D4ED8)', transform: { base: 'none', md: 'translateY(-1px)' } }} _disabled={{ bg: 'gray.300', color: 'gray.500', transform: 'none', cursor: 'not-allowed', opacity: 0.6 }}>
                  ← Попередня
                </Button>
                {visiblePages.map((page, index) => {
                  if (typeof page !== 'number') {
                    return (
                      <Box key={`${page}-${index}`} px={2} color={textColor}>
                        …
                      </Box>
                    );
                  }

                  const isActive = page === pagination.currentPage;
                  return (
                    <Button
                      key={page}
                      onClick={() => goToPage(page)}
                      size={{ base: 'sm', md: 'md' }}
                      minW={{ base: '36px', md: '40px' }}
                      px={{ base: 2, md: 3 }}
                      bg={isActive ? 'linear-gradient(135deg, #10B981, #059669)' : bgColor}
                      color={isActive ? 'white' : textColor}
                      border="1px solid"
                      borderColor={isActive ? 'transparent' : borderColor}
                      borderRadius="lg"
                      _hover={{
                        bg: isActive ? 'linear-gradient(135deg, #059669, #047857)' : borderColor,
                        transform: { base: 'none', md: 'translateY(-1px)' },
                      }}
                      isDisabled={isActive}
                    >
                      {page}
                    </Button>
                  );
                })}
                <Button onClick={() => fetchNext()} isDisabled={!pagination.next} bg="linear-gradient(135deg, #3B82F6, #2563EB)" color="white" borderRadius="xl" size={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 6 }} _hover={{ bg: 'linear-gradient(135deg, #2563EB, #1D4ED8)', transform: { base: 'none', md: 'translateY(-1px)' } }} _disabled={{ bg: 'gray.300', color: 'gray.500', transform: 'none', cursor: 'not-allowed', opacity: 0.6 }}>
                  Наступна →
                </Button>
              </HStack>
            </Flex>
          </Box>
        )}

        <InvoiceModal
          key={modalKey} // Force remount on key change
          isOpen={isCreateModalOpen}
          onClose={handleCloseModal}
          type={"return"}
          invoiceData={editingInvoice}
          onInvoiceCreated={handleInvoiceCreated}
        />

        {/* Invoice Details Modal */}
        {selectedInvoice && (
          <Modal
            isOpen={isDetailsModalOpen}
            onClose={handleCloseDetailsModal}
            size="4xl"
            isCentered
          >
            <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
            <ModalContent>
              <ModalHeader>Деталі накладної</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {selectedInvoice ? (
                <Box>
                  {/* Invoice Header Info - Always show this immediately */}
                  <Box
                    bg="red.50"
                    p={4}
                    borderRadius="xl"
                    mb={6}
                    border="1px solid"
                    borderColor="red.200"
                  >
                    <Text fontWeight="bold" fontSize="lg" color="red.700" mb="2">
                      Накладна повернення #{selectedInvoice.id}
                    </Text>
                    <Text color="gray.700">
                      Дата створення: {new Date(selectedInvoice.created_at).toLocaleString()}
                    </Text>
                    <Text color="gray.700">
                      Клієнт: {selectedInvoice.customer_name || 'Без клієнта'}
                    </Text>
                    <Text color="gray.700">
                      Примітка: {selectedInvoice.note || 'Без приміток'}
                    </Text>
                  </Box>

                  {/* Product List */}
                  {selectedInvoice.entries && selectedInvoice.entries.length > 0 ? (
                  <Box
                    maxH="400px"
                    overflowY="auto"
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={borderColor}
                    bg={bgColor}
                  >
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                        <Tr>
                          <Th>Назва товару</Th>
                          <Th>Закупочна ціна</Th>
                          <Th>Відсоток націнки</Th>
                          <Th>Ціна за одиницю</Th>
                          <Th>Кількість</Th>
                          <Th>Загальна ціна</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {selectedInvoice.entries.map((entry) => (
                          <Tr key={entry.id}>
                            <Td 
                              fontWeight="medium"
                              cursor="pointer" 
                              _hover={{ 
                                bg: productHoverBg,
                                textDecoration: 'underline'
                              }}
                              onClick={() => handleOpenProductDetail(entry.product)}
                            >
                              {entry.product.name}
                            </Td>
                            <Td>
                              {entry.purchase_price} грн
                            </Td>
                            <Td>
                              {entry.markup_percentage} %
                            </Td>
                            <Td>
                              {entry.price} грн
                            </Td>
                            <Td>
                              {entry.quantity}
                            </Td>
                            <Td fontWeight="semibold">
                              {(parseFloat(entry.price) * parseInt(entry.quantity)).toFixed(2)} грн
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  ) : (
                    <Box textAlign="center" p={8}>
                      <Text color="gray.700">
                        Немає товарів у цій накладній
                      </Text>
                    </Box>
                  )}

                  {/* Totals */}
                  {selectedInvoice.entries && selectedInvoice.entries.length > 0 && (
                    <Box 
                      textAlign="right" 
                      mt="6" 
                      borderTop="1px solid"
                      borderColor={borderColor}
                      pt="4"
                    >
                      <Box
                        bg="green.50"
                        p={4}
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="green.200"
                      >
                        <Text color="gray.700" mb={1}>
                          Разом без ПДВ: {totalWithoutVat.toFixed(2)} грн
                        </Text>
                        <Text color="gray.700" mb={1}>
                          ПДВ: {selectedInvoice.entries.some((entry) => entry.product.is_vat) ? vat.toFixed(2) : '0.00'} грн
                        </Text>
                        <Text 
                          fontWeight="bold" 
                          fontSize="lg"
                          color="green.700"
                        >
                          Всього з ПДВ: {selectedInvoice.entries.some((entry) => entry.product.is_vat) ? totalWithVat.toFixed(2) : totalWithoutVat.toFixed(2)} грн
                        </Text>
                      </Box>
                    </Box>
                  )}
                </Box>
                ) : (
                  <Flex justify="center" align="center" minH="400px">
                    <Box textAlign="center">
                      <Spinner 
                        size="xl" 
                        color="blue.500"
                        thickness="4px"
                        mb={4}
                      />
                      <Text color="gray.700">
                        Завантаження накладної...
                      </Text>
                    </Box>
                  </Flex>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
        )}

        {/* Product Detail Modal */}
        <ProductDetailModal
          isOpen={isProductDetailModalOpen}
          onClose={handleCloseProductDetail}
          product={selectedProduct}
        />
      </Box>
    </Box>
  );
};

export default ReturnInvoicesPage;